import { createRouter, createWebHistory } from 'vue-router'
import { getUnixTime } from 'date-fns'
import { useAuth } from '@/composables/useAuth'

// Every page is loaded on demand. Charting and table libraries are heavy, and
// there is no reason for the login screen to carry the admin panel's weight —
// or anyone's browser to download the charts until they open a page with one.
const Play = () => import('@/pages/Play.vue')
const Login = () => import('@/pages/Login.vue')
const ForgotPassword = () => import('@/pages/ForgotPassword.vue')
const ResetPassword = () => import('@/pages/ResetPassword.vue')
const Matchdays = () => import('@/pages/Matchdays.vue')
const MatchdayRecap = () => import('@/pages/MatchdayRecap.vue')
const Ranking = () => import('@/pages/Ranking.vue')
const Statistics = () => import('@/pages/Statistics.vue')
const Rules = () => import('@/pages/Rules.vue')
const Profile = () => import('@/pages/Profile.vue')
const Admin = () => import('@/pages/Admin.vue')

// Pages own their own headers, so meta.title is here only for the document
// title — the shell no longer renders it.
const routes = [
    { path: '/', name: 'Home', component: Play, meta: { title: 'Jugar' } },
    { path: '/login', name: 'TheLogin', component: Login, meta: { title: 'Entrar' } },
    {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: ForgotPassword,
        meta: { title: 'Recuperar acceso' }
    },
    {
        path: '/reset-password',
        name: 'ResetPassword',
        component: ResetPassword,
        meta: { title: 'Nueva contraseña' }
    },
    { path: '/matchdays', name: 'Matchdays', component: Matchdays, meta: { title: 'Jornadas' } },
    {
        path: '/matchdays/:number/recap',
        name: 'MatchdayRecap',
        component: MatchdayRecap,
        meta: { title: 'Recap de jornada' }
    },
    { path: '/ranking', name: 'Ranking', component: Ranking, meta: { title: 'Ranking' } },
    { path: '/statistics', name: 'Statistics', component: Statistics, meta: { title: 'Mis estadísticas' } },
    { path: '/rules', name: 'GameRules', component: Rules, meta: { title: 'Reglas' } },
    { path: '/profile', name: 'Profile', component: Profile, meta: { title: 'Mi perfil' } },
    // The old app split viewing and editing the profile across two routes.
    // Keep the URL working so bookmarks and emails do not break.
    { path: '/profile/edit', redirect: '/profile' },
    {
        path: '/admin',
        name: 'Admin',
        component: Admin,
        meta: { title: 'Panel de administración', requiresAdmin: true }
    },
    { path: '/:pathMatch(.*)*', redirect: '/' }
]

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior: () => ({ top: 0 })
})

const PUBLIC_ROUTES = ['TheLogin', 'ForgotPassword', 'ResetPassword']

router.beforeEach((to, _from, next) => {
    const { currentUser, logout, isAdmin } = useAuth()

    if (!currentUser.value) {
        return PUBLIC_ROUTES.includes(to.name) ? next() : next({ name: 'TheLogin' })
    }

    // An expired token is as good as no session.
    if (currentUser.value.user?.expiration < getUnixTime(new Date())) {
        logout()
        return PUBLIC_ROUTES.includes(to.name) ? next() : next({ name: 'TheLogin' })
    }

    // A signed-in player has no business on the login screen. Without this the
    // form rendered inside the app shell and submitting it did nothing.
    if (to.name === 'TheLogin') return next({ name: 'Home' })

    if (to.meta.requiresAdmin && !isAdmin.value) return next({ name: 'Home' })

    next()
})

router.afterEach((to) => {
    const title = to.meta?.title
    document.title = title ? `${title} · Quiniela` : 'Quiniela'
})

export default router
